/**
 * PB Pick & Mix — Planet B brand switcher + subcollection filter
 *
 * - Brand tabs (WONDR / POWR / SMYLE) switch which category collections are visible
 * - Default brand comes from the section's "Default brand" setting, and can be
 *   overridden per visit with ?pm_brand=powr so campaigns can deep-link into a
 *   specific brand without changing the setting for everyone
 * - Brand switch scrolls to the first collection of the selected brand
 * - Subcollection clicks filter the grid, then scroll the category into view
 *   under the sticky brand switcher + category bar
 * - Each brand can have an info bar ("WONDR = SELFCARE • ...") that switches
 *   along with the tabs and that visitors can collapse and expand again
 */
(function () {
  const ROOT_SELECTOR = '[data-pb-pick-mix][data-pm-planetb]';
  const BRAND_TAB_SELECTOR = '[data-pm-brand-tab]';
  const BRAND_NAV_SELECTOR = '[data-pm-brand-nav]';
  const LINK_SELECTOR = '.pb-pm__nav-link';
  const SHOW_ALL_SELECTOR = '[data-pm-show-all]';
  const CATEGORY_SELECTOR = '.pb-pm__category';
  const BRAND_INFO_SELECTOR = '[data-pm-brand-info]';
  const BRAND_INFO_TOGGLE_SELECTOR = '[data-pm-brand-info-toggle]';
  const TOP_BLOCK_SELECTOR = '[data-pm-tp-top]';
  const BRAND_ONLY_SELECTOR = '[data-pm-brand-only]';
  // Also read by the inline script in pb-pick-mix-bundle-planetb.liquid.
  const BRAND_INFO_STORAGE_PREFIX = 'pbpm-brand-info-collapsed:';
  const BRAND_INFO_COLLAPSED_CLASS = 'is-collapsed';
  const ACTIVE_CLASS = 'is-filter-active';
  const BRAND_ACTIVE_CLASS = 'is-brand-active';
  const BRAND_PARAM = 'pm_brand';

  function getRoots() {
    // Every root on the page, not just the first: with the Convert A/B setup
    // the control and C1 sections coexist and both need working brand tabs.
    return Array.from(document.querySelectorAll(ROOT_SELECTOR));
  }

  function getBrand(root) {
    return root.dataset.pmActiveBrand || 'wondr';
  }

  function setBrand(root, brand) {
    root.dataset.pmActiveBrand = brand;
  }

  function getAvailableBrands(root) {
    return Array.from(root.querySelectorAll(BRAND_TAB_SELECTOR))
      .map((tab) => (tab.dataset.pmBrandTab || '').toLowerCase())
      .filter(Boolean);
  }

  /**
   * Brand requested via ?pm_brand=..., or null when absent/unknown so the
   * section's default brand stays in charge. Validated against the tabs that
   * are actually rendered, so adding or removing a brand needs no change here.
   */
  function getBrandFromUrl(root) {
    let requested = null;

    try {
      requested = new URLSearchParams(window.location.search).get(BRAND_PARAM);
    } catch {
      return null;
    }

    if (!requested) return null;

    const brand = requested.trim().toLowerCase();
    return getAvailableBrands(root).includes(brand) ? brand : null;
  }

  function isShowAllLink(link) {
    return link instanceof HTMLElement && link.hasAttribute('data-pm-show-all');
  }

  function matchesBrand(el, brand) {
    const elBrand = (el.dataset.pmBrand || 'wondr').toLowerCase();
    return elBrand === brand;
  }

  function getBrandLinks(root, brand) {
    return Array.from(root.querySelectorAll(LINK_SELECTOR)).filter((link) => {
      if (isShowAllLink(link)) return true;
      return matchesBrand(link, brand);
    });
  }

  function getCategoryForLink(root, link) {
    if (isShowAllLink(link)) return null;

    const href = link.getAttribute('href') || '';
    if (!href.startsWith('#') || href.length < 2) return null;

    const id = href.slice(1);
    const category = root.querySelector(`#${CSS.escape(id)}`);
    if (!(category instanceof HTMLElement)) return null;

    return category;
  }

  function getBrandNavOffset(root, gap = 12) {
    const brandNav = root.querySelector(BRAND_NAV_SELECTOR);
    if (!(brandNav instanceof HTMLElement)) return gap;

    const stickyTop = parseFloat(getComputedStyle(brandNav).top) || 0;
    return brandNav.getBoundingClientRect().height + stickyTop + gap;
  }

  function scrollCategoryIntoView(root, target, gap = 12) {
    if (!(target instanceof HTMLElement)) return;

    // Wait a frame so filter visibility/layout settles before measuring.
    requestAnimationFrame(() => {
      const top = target.getBoundingClientRect().top + window.scrollY - getBrandNavOffset(root, gap);

      window.scrollTo({
        top: Math.max(0, top),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    });
  }

  /**
   * ?pm_focus=<variant-id> (the "Bundel & bespaar" link on the product card)
   * scrolls to that product's card. ?pm_focus=<product-handle> does the same
   * for a product found by handle: the card links an original shampoo bar
   * that is not in the Pick & Mix to its XL version this way. The card's
   * brand wins over ?pm_brand, so the product is never hidden behind another
   * brand tab. The parameter is removed from the URL so a reload doesn't jump
   * again. A product that is not in the Pick & Mix is ignored: the page stays
   * on top.
   */
  const FOCUS_PARAM = 'pm_focus';

  function takeFocusFromUrl() {
    let value = '';

    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has(FOCUS_PARAM)) return '';
      value = (url.searchParams.get(FOCUS_PARAM) || '').trim();
      url.searchParams.delete(FOCUS_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch {
      return '';
    }

    return value;
  }

  function findFocusCard(root, focus) {
    if (!focus) return null;

    let card = null;
    // The hidden card behind a GIFTS-tab banner (data-gifts-ed-card) is never the target.
    const notBanner = ':not([data-gifts-ed-card] [data-pm-card])';

    if (/^\d+$/.test(focus)) {
      card =
        root.querySelector(`[data-pm-card][data-variant-id="${focus}"]${notBanner}`) ||
        root.querySelector(`[data-pm-variant-swatch][data-variant-id="${focus}"]`)?.closest('[data-pm-card]');
    } else {
      // data-product-url is product.url, possibly with a locale prefix.
      card = root.querySelector(`[data-pm-card][data-product-url$="/products/${CSS.escape(focus)}"]${notBanner}`);
    }

    return card instanceof HTMLElement ? card : null;
  }

  function focusCard(root, card) {
    const category = card.closest(CATEGORY_SELECTOR);
    const brand = category instanceof HTMLElement ? (category.dataset.pmBrand || 'wondr').toLowerCase() : null;

    if (brand && brand !== getBrand(root)) {
      setBrand(root, brand);
      activateDefaultForBrand(root);
    }

    const scroll = () => scrollCategoryIntoView(root, card);
    scroll();

    // Images above the card can still shift the layout while the page loads;
    // correct once on load, unless the visitor has scrolled away themselves.
    if (document.readyState !== 'complete') {
      let userScrolled = false;
      const markUserScroll = () => {
        userScrolled = true;
      };
      window.addEventListener('wheel', markUserScroll, { once: true, passive: true });
      window.addEventListener('touchstart', markUserScroll, { once: true, passive: true });
      window.addEventListener(
        'load',
        () => {
          if (!userScrolled) scroll();
        },
        { once: true },
      );
    }
  }

  function setSolydMode(mode) {
    for (const root of getRoots()) {
      root.dataset.pmFilterMode = mode;
    }
  }

  /**
   * The collapsed state of the brand info bar is remembered in localStorage.
   * The key includes a hash of the copy (set in the Liquid), so new copy gets a
   * new key and shows expanded again — also for visitors who collapsed the
   * previous version. Storage can throw in private mode; the bar then simply
   * starts expanded every time.
   */
  function isBrandInfoCollapsedStored(key) {
    if (!key) return false;

    try {
      return window.localStorage.getItem(BRAND_INFO_STORAGE_PREFIX + key) === '1';
    } catch {
      return false;
    }
  }

  function rememberBrandInfoCollapsed(key, collapsed) {
    if (!key) return;

    try {
      if (collapsed) {
        window.localStorage.setItem(BRAND_INFO_STORAGE_PREFIX + key, '1');
      } else {
        window.localStorage.removeItem(BRAND_INFO_STORAGE_PREFIX + key);
      }
    } catch {
      /* no storage available — the state then only lasts for this page */
    }
  }

  function setBrandInfoCollapsed(bar, collapsed) {
    bar.classList.toggle(BRAND_INFO_COLLAPSED_CLASS, collapsed);

    for (const toggle of bar.querySelectorAll(BRAND_INFO_TOGGLE_SELECTOR)) {
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }
  }

  function updateBrandInfo(root, brand) {
    for (const bar of root.querySelectorAll(BRAND_INFO_SELECTOR)) {
      bar.hidden = !matchesBrand(bar, brand);
    }

    // Other blocks for one tab only, e.g. the Gifts tab banner.
    for (const el of root.querySelectorAll(BRAND_ONLY_SELECTOR)) {
      el.hidden = !matchesBrand(el, brand);
    }
  }

  // Init only: re-reading storage on every brand switch would, without storage
  // (private mode), re-expand a bar the visitor just collapsed.
  function restoreBrandInfo(root) {
    for (const bar of root.querySelectorAll(BRAND_INFO_SELECTOR)) {
      setBrandInfoCollapsed(bar, isBrandInfoCollapsedStored(bar.dataset.pmInfoKey));
    }
  }

  function toggleBrandInfo(bar, toggle) {
    const collapse = toggle.dataset.pmBrandInfoToggle === 'collapse';
    const hadFocus = document.activeElement === toggle;

    setBrandInfoCollapsed(bar, collapse);
    rememberBrandInfoCollapsed(bar.dataset.pmInfoKey, collapse);

    // The clicked button disappears; hand focus to its counterpart so keyboard
    // users don't fall back to the top of the page.
    if (hadFocus) {
      const counterpart = bar.querySelector(
        `[data-pm-brand-info-toggle="${collapse ? 'expand' : 'collapse'}"]`
      );
      if (counterpart instanceof HTMLElement) counterpart.focus({ preventScroll: true });
    }
  }

  function updateBrandTabs(root, brand) {
    for (const tab of root.querySelectorAll(BRAND_TAB_SELECTOR)) {
      const isActive = (tab.dataset.pmBrandTab || '').toLowerCase() === brand;
      tab.classList.toggle(BRAND_ACTIVE_CLASS, isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }
  }

  function setActiveFilter(root, link, { scroll = false } = {}) {
    const brand = getBrand(root);
    const links = getBrandLinks(root, brand);
    const categories = Array.from(root.querySelectorAll(CATEGORY_SELECTOR));
    const showAll = isShowAllLink(link);

    setSolydMode(showAll ? 'all' : 'filter');

    for (const item of root.querySelectorAll(LINK_SELECTOR)) {
      const inBrand = isShowAllLink(item) || matchesBrand(item, brand);
      item.hidden = !inBrand;
      item.classList.toggle(ACTIVE_CLASS, inBrand && item === link);
      if (inBrand) {
        item.setAttribute('aria-current', item === link ? 'true' : 'false');
      } else {
        item.removeAttribute('aria-current');
        item.classList.remove(ACTIVE_CLASS);
      }
    }

    const target = getCategoryForLink(root, link);

    for (const category of categories) {
      const inBrand = matchesBrand(category, brand);
      const shouldShow = inBrand && (showAll || (target != null && category === target));
      category.hidden = !shouldShow;
      category.classList.toggle(ACTIVE_CLASS, shouldShow);
    }

    // Keep show-all link in the visible set for this brand
    for (const item of links) {
      if (!isShowAllLink(item)) continue;
      item.hidden = false;
    }

    if (scroll) {
      scrollCategoryIntoView(root, showAll ? root.querySelector('.pb-pm__categories') : target);
    }
  }

  function getFirstBrandCategory(root, brand) {
    return Array.from(root.querySelectorAll(CATEGORY_SELECTOR)).find((category) => {
      return matchesBrand(category, brand) && category instanceof HTMLElement;
    }) || null;
  }

  function activateDefaultForBrand(root) {
    const brand = getBrand(root);
    const showAll = root.querySelector(SHOW_ALL_SELECTOR);
    const links = getBrandLinks(root, brand).filter((link) => !isShowAllLink(link) || showAll);

    updateBrandTabs(root, brand);
    updateBrandInfo(root, brand);

    if (showAll instanceof HTMLElement) {
      setActiveFilter(root, showAll, { scroll: false });
      return;
    }

    if (links.length) {
      // No explicit "show all" — show every category for the brand
      setSolydMode('all');
      for (const item of root.querySelectorAll(LINK_SELECTOR)) {
        const inBrand = matchesBrand(item, brand);
        item.hidden = !inBrand;
        item.classList.remove(ACTIVE_CLASS);
        item.removeAttribute('aria-current');
      }
      for (const category of root.querySelectorAll(CATEGORY_SELECTOR)) {
        const inBrand = matchesBrand(category, brand);
        category.hidden = !inBrand;
        category.classList.toggle(ACTIVE_CLASS, inBrand);
      }
    }
  }

  function switchBrand(root, brand) {
    setBrand(root, brand);
    activateDefaultForBrand(root);

    // A brand switch lands on the brand's info bar ("WONDR = SELFCARE"),
    // flush under the sticky nav, so it is never scrolled past. Without a bar,
    // on a block shown above every brand's categories (the Think Pink buy
    // block), and otherwise on the brand's first category. A hidden top block
    // (display: none) has no offsetParent and is skipped.
    const infoBar = Array.from(root.querySelectorAll(BRAND_INFO_SELECTOR)).find(
      (bar) => bar instanceof HTMLElement && !bar.hidden && matchesBrand(bar, brand)
    );
    if (infoBar) {
      scrollCategoryIntoView(root, infoBar, 0);
      return;
    }

    const topBlock = root.querySelector(TOP_BLOCK_SELECTOR);
    const target =
      topBlock instanceof HTMLElement && topBlock.offsetParent !== null
        ? topBlock
        : getFirstBrandCategory(root, brand);

    if (target) {
      scrollCategoryIntoView(root, target);
    }
  }

  function onClick(event) {
    const root = event.target.closest(ROOT_SELECTOR);
    if (!(root instanceof HTMLElement)) return;

    const infoToggle = event.target.closest(BRAND_INFO_TOGGLE_SELECTOR);
    if (infoToggle instanceof HTMLElement && root.contains(infoToggle)) {
      const bar = infoToggle.closest(BRAND_INFO_SELECTOR);
      if (bar instanceof HTMLElement) {
        event.preventDefault();
        toggleBrandInfo(bar, infoToggle);
      }
      return;
    }

    const brandTab = event.target.closest(BRAND_TAB_SELECTOR);
    if (brandTab instanceof HTMLElement && root.contains(brandTab)) {
      event.preventDefault();
      const brand = (brandTab.dataset.pmBrandTab || 'wondr').toLowerCase();
      if (brand !== getBrand(root)) {
        switchBrand(root, brand);
      }
      return;
    }

    const link = event.target.closest(LINK_SELECTOR);
    if (!(link instanceof HTMLElement) || !root.contains(link)) return;
    if (link.hidden) return;

    event.preventDefault();
    setActiveFilter(root, link, { scroll: true });
  }

  function onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const brandTab = event.target.closest(BRAND_TAB_SELECTOR);
    if (!(brandTab instanceof HTMLElement)) return;

    const root = brandTab.closest(ROOT_SELECTOR);
    if (!(root instanceof HTMLElement)) return;

    event.preventDefault();
    const brand = (brandTab.dataset.pmBrandTab || 'wondr').toLowerCase();
    if (brand !== getBrand(root)) {
      switchBrand(root, brand);
    }
  }

  function init() {
    const focus = takeFocusFromUrl();
    let focused = false;

    for (const root of getRoots()) {
      if (root.dataset.pmFilterBound !== 'true') {
        root.dataset.pmFilterBound = 'true';
        root.addEventListener('click', onClick);
        root.addEventListener('keydown', onKeydown);
      }

      const defaultBrand = (root.dataset.pmDefaultBrand || 'wondr').toLowerCase();
      setBrand(root, getBrandFromUrl(root) || defaultBrand);
      activateDefaultForBrand(root);
      restoreBrandInfo(root);

      // Only the first visible Pick & Mix that has the product scrolls; with
      // the control and C1 sections side by side one of them is hidden.
      if (!focused && root.offsetParent !== null) {
        const card = findFocusCard(root, focus);
        if (card) {
          focusCard(root, card);
          focused = true;
        }
      }

      // Only enable transitions after a paint with the stored state; otherwise
      // a bar that was collapsed earlier visibly collapses on page load.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.dataset.pmInfoReady = 'true';
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.PBPickMixPlanetBFilter = { init, switchBrand };
})();
